import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { DateTimePicker } from '../../../../../components/atoms/DateTimePicker';
import { getCurrentTime } from '../../../../../utils/common';

export interface CheckInDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (travelStart: string, onSiteTime: string, notes: string) => void;
  submitting: boolean;
  /** Pre-filled travel start from service.travelStartTime (YYYY-MM-DD HH:mm). Empty string if not available. */
  travelStartTime?: string;
}

export const CheckInDialog: React.FC<CheckInDialogProps> = ({
  open,
  onClose,
  onSubmit,
  submitting,
  travelStartTime = '',
}) => {
  const theme = useTheme();
  const [travelStart, setTravelStart] = useState('');
  const [onSiteTime, setOnSiteTime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setTravelStart(travelStartTime);
      setOnSiteTime(getCurrentTime());
      setNotes('');
    }
  }, [open, travelStartTime]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Check In</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
              Travel Start Time (optional)
            </Typography>
            <DateTimePicker
              label="Travel Start"
              value={travelStart}
              disableFuture
              onChange={setTravelStart}
            />
          </Stack>
          <Stack spacing={0.5}>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>On Site Time</Typography>
            <DateTimePicker
              label="On Site Time"
              value={onSiteTime}
              disableFuture
              onChange={setOnSiteTime}
            />
          </Stack>
          <TextField
            label="Notes (optional)"
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(travelStart, onSiteTime, notes)}
          disabled={submitting}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Check In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
